import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, Award } from "lucide-react";
import { cn } from "@nexus/ui";
import { canAnimate } from "@/lib/motion";
import {
  buildBenchmarkDelta,
  formatBrandName,
  formatCountValue,
} from "../../lib/format";
import { BrandMark } from "../BrandMark/BrandMark";

export interface CompetitorKpiCardProps {
  title: string;
  you: number;
  competitor: number;
  benchmark: number;
  youLabel?: string;
  competitorLabel?: string;
  youLogoUrl?: string | null;
  competitorLogoUrl?: string | null;
  isLoading?: boolean;
  skeletonDelayMs?: number;
  animationKey?: string;
  isSelected?: boolean;
  isClickable?: boolean;
  onClick?: () => void;
  /** Fires on mouse enter/leave and keyboard focus/blur. */
  onHoverChange?: (isHovered: boolean) => void;
}

const ANIMATION_MS = 900;

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function useCountUp(
  target: number,
  duration: number,
  enabled: boolean,
  animationKey?: string,
): number {
  const [value, setValue] = useState(() => (enabled && !canAnimate() ? target : 0));
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      setValue(0);
      return;
    }

    if (!canAnimate()) {
      setValue(target);
      return;
    }

    let cancelled = false;
    setValue(0);
    const start = performance.now();

    const tick = (now: number) => {
      if (cancelled) return;
      const progress = Math.min((now - start) / duration, 1);
      setValue(target * easeOutCubic(progress));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      }
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, [target, duration, enabled, animationKey]);

  return value;
}

export function CompetitorKpiCard({
  title,
  you,
  competitor,
  benchmark,
  youLabel,
  competitorLabel,
  youLogoUrl,
  competitorLogoUrl,
  isLoading = false,
  skeletonDelayMs = 0,
  animationKey,
  isSelected = false,
  isClickable = false,
  onClick,
  onHoverChange,
}: CompetitorKpiCardProps) {
  const { t } = useTranslation();
  const ready = !isLoading;
  const animatedYou = useCountUp(you, ANIMATION_MS, ready, animationKey);
  const delta = buildBenchmarkDelta(you, benchmark);

  const resolvedYouLabel = formatBrandName(youLabel ?? t("dashboard.kpiCards.you"));
  const resolvedCompetitorLabel = formatBrandName(
    competitorLabel ?? t("dashboard.kpiCards.competitor"),
  );

  if (isLoading) {
    return (
      <div
        className="flex flex-col gap-3 rounded-xl border bg-card p-4"
        style={{ "--shimmer-delay": `${skeletonDelayMs}ms` } as React.CSSProperties}
        aria-busy
      >
        <div className="skeleton-shimmer-bar h-3.5 w-24 rounded-md" />
        <div className="flex items-center gap-2">
          <div className="skeleton-shimmer-bar h-7 w-16 rounded-md" />
          <div className="skeleton-shimmer-bar h-6 w-28 rounded-full" />
        </div>
        <div className="skeleton-shimmer-bar mt-auto h-4 w-full max-w-[12rem] rounded-md" />
      </div>
    );
  }

  const displayYou = formatCountValue(animatedYou);
  const headlineAria = t("dashboard.kpiCards.yourValueAria", {
    brand: resolvedYouLabel,
    value: formatCountValue(you),
  });

  function handleKeyDown(event: React.KeyboardEvent) {
    if (!isClickable || !onClick) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  }

  return (
    <div
      className={cn(
        "flex h-full flex-col rounded-xl border bg-card p-4",
        isClickable && "cursor-pointer transition-shadow hover:ring-2 hover:ring-ring/40",
        isSelected && "ring-2 ring-primary",
      )}
      data-competitor-kpi-card
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-pressed={isClickable ? isSelected : undefined}
      onClick={isClickable ? onClick : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      onMouseEnter={onHoverChange ? () => onHoverChange(true) : undefined}
      onMouseLeave={onHoverChange ? () => onHoverChange(false) : undefined}
      onFocus={onHoverChange ? () => onHoverChange(true) : undefined}
      onBlur={onHoverChange ? () => onHoverChange(false) : undefined}
    >
      <p className="pb-3 text-[13px] leading-[19.5px] tracking-[-0.0762px] text-foreground">
        {title}
      </p>

      <div className="flex flex-wrap items-center gap-2 pb-3">
        <span className="inline-flex items-center gap-1.5">
          <BrandMark label={resolvedYouLabel} logoUrl={youLogoUrl} kind="you" />
          <span
            className="text-[22px] font-semibold leading-[22px] tracking-[-0.2578px] tabular-nums text-foreground"
            aria-label={headlineAria}
          >
            {displayYou}
          </span>
        </span>

        {competitor > 0 && (
          <div className="inline-flex items-center gap-1 rounded-full border bg-muted px-2 py-0.5">
            <span className="text-[11px] leading-[16.5px] tracking-[0.0645px] text-muted-foreground">
              {t("dashboard.kpiCards.vs")}
            </span>
            <BrandMark
              label={resolvedCompetitorLabel}
              logoUrl={competitorLogoUrl}
              kind="competitor"
            />
            <span className="text-xs tabular-nums text-foreground">
              {formatCountValue(competitor)}
            </span>
          </div>
        )}
      </div>

      <div className="mt-auto flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs leading-[18px]">
        {delta.hasBenchmark ? (
          <>
            <span
              className={cn(
                "inline-flex items-center gap-1 font-medium tabular-nums",
                delta.isPositive
                  ? "text-emerald-700 dark:text-emerald-400"
                  : "text-destructive",
              )}
            >
              {delta.isPositive ? (
                <ArrowUp className="size-3.5 shrink-0" aria-hidden />
              ) : (
                <ArrowDown className="size-3.5 shrink-0" aria-hidden />
              )}
              {delta.isPositive ? "+" : ""}
              {Math.round(delta.percent)}%
            </span>
            <span className="text-muted-foreground">
              {t("dashboard.kpiCards.vsBenchmark", {
                value: formatCountValue(benchmark),
              })}
            </span>
            <Award className="size-3 shrink-0 text-muted-foreground" aria-hidden />
          </>
        ) : (
          <span className="text-muted-foreground">{t("dashboard.kpiCards.noBenchmark")}</span>
        )}
      </div>
    </div>
  );
}
